import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero, Countdown } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { SCHOLARSHIP_DATE, SCHOLARSHIP_LABEL, IMAGES } from '../content/data';

const TRACKS = [
  {
    to: '/programmes/flagship',
    tag: '18-Month Flagship Programme',
    title: 'The Complete DITEC Journey',
    desc: 'Technology + digital marketing + AI + analytics + cybersecurity + project management + commercial application + entrepreneurship.',
    items: ['Six structured phases', 'Four digital assets', 'Industry internship', 'Capstone defence'],
    cta: 'Explore 18-Month Programme',
    dark: true,
  },
  {
    to: '/programmes/technical',
    tag: '9-Month Technical Excellence Track',
    title: 'For the Technology-Focused Learner',
    desc: 'Web development, cybersecurity, analytics, AI augmentation, technical SEO, auditing and a defended technical portfolio.',
    items: ['Web development', 'Cybersecurity', 'Analytics', 'AI augmentation', 'Technical SEO', 'Auditing', 'Technical portfolio'],
    cta: 'Explore Technical Track',
  },
  {
    to: '/programmes/business-creator',
    tag: '9-Month Business & Creator Track',
    title: 'For the Learner Focused on Audience, Marketing and Income',
    desc: 'Brand building, content, SEO, advertising, sales, lead generation, AI for business, monetization, freelancing and entrepreneurship.',
    items: ['Brand building', 'Content', 'SEO', 'Advertising', 'Sales', 'Lead generation', 'AI for business', 'Monetization', 'Freelancing', 'Entrepreneurship'],
    cta: 'Explore Business & Creator Track',
  },
];

export default function Programmes() {
  const { open } = useLead();
  const { settings } = useContent();
  const schDate = settings.scholarshipDate || SCHOLARSHIP_DATE;
  const schLabel = settings.scholarshipLabel || SCHOLARSHIP_LABEL;
  return (
    <div data-testid="programmes-page">
    <SEO title="Programmes" description="The 18-Month Flagship and two focused 9-Month tracks — every pathway builds evidence, not just certificates." />
      <PageHero
        ck="programmes"
        tag="Programmes"
        lines={<>Three Pathways. <span className="font-serif italic font-normal text-electric">One Destination: Evidence.</span></>}
        intro="Every DITEC programme is built around practical output — live digital properties, real projects and measurable performance that speak for you."
        image={IMAGES.coding}
      />

      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap space-y-8">
          {TRACKS.map((t, i) => (
            <Reveal key={t.to} delay={i * 0.06}>
              <div data-testid={`track-card-${i}`} className={`grid gap-8 rounded-3xl p-8 sm:p-12 lg:grid-cols-[1.2fr_1fr] ${t.dark ? 'bg-navy text-white' : 'border border-navy/10 bg-white'}`}>
                <div>
                  <span className={`overline !text-[10px] ${t.dark ? 'text-gold' : 'text-electric'}`}>{t.tag}</span>
                  <h2 className="text-h3 mt-4 font-semibold">{t.title}</h2>
                  <p className={`mt-4 text-sm leading-relaxed sm:text-base ${t.dark ? 'text-white/70' : 'text-navy/70'}`}>{t.desc}</p>
                  <div className="mt-7"><ArrowBtn to={t.to} variant={t.dark ? 'gold' : 'navy'} testId={`track-cta-${i}`}>{t.cta}</ArrowBtn></div>
                </div>
                <ul className={`grid content-start gap-2.5 sm:grid-cols-2 ${t.dark ? 'text-white/80' : 'text-navy/80'}`}>
                  {t.items.map((x) => (
                    <li key={x} className="flex items-center gap-2.5 text-sm font-medium">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.dark ? 'bg-gold' : 'bg-electric'}`} />{x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Scholarship strip */}
      <section className="bg-gold py-16 text-navy sm:py-20">
        <div className="wrap grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <SectionTag>Annual Scholarship Test</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-4 font-semibold">Merit should never wait on <span className="font-serif italic font-normal">money.</span></h2></Reveal>
            <Reveal delay={0.1} className="mt-7 flex flex-wrap gap-4">
              <ArrowBtn to="/scholarship" variant="navy" testId="programmes-cta-scholarship">View Scholarship Details</ArrowBtn>
              <ArrowBtn onClick={() => open('Scholarship')} variant="ghostDark" testId="programmes-cta-register">Register for the Test</ArrowBtn>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="rounded-3xl bg-navy p-7 text-white sm:p-8">
            <span className="overline !text-[10px] text-gold">Next test — {schLabel}</span>
            <div className="mt-5"><Countdown target={schDate} dark testId="programmes-countdown" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
